import React, { useEffect } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Stack,
  Box,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TemplateItem from './Items/TemplateItem';
import { TemplatePreview } from './SectionRenderer.style';
import ConnectIcon from 'components/Icons/connectIcon';
import { STEP_TYPES } from 'utils/templates';

interface SectionRendererProps {
  section: any;
  sectionScores: any;
  localChecklist: any;
  isLogSubmitted: boolean;
  onResponseToItem: (item: any, value: any) => void;
  automations: any;
  childItemsMap: any;
  lastUpdatedRef: any;
  onNotePostToItem: any;
  handleUpdateItemNote: any;
  handleDeleteChecklistItemNote: any;
  handleCreateCorrectiveTask: any;
  itemErrors: any;
  handleShowUploadModal: any;
  isActionsAllowed: boolean;
  taskLocation: string;
  isMobileDeviceMd: boolean;
  checkVisibility?: (item: any) => boolean;
  parentSection?: any;
  checkListLogHashes?: any;
  checklistProgAndScore?: any;
  isSectionVisible?: boolean;
  updateMultipleItems?: (items: any, value: any) => void;
  isReviewMode?: boolean;
  canReview?: boolean;
  feedbackActions?: any;
}

const SectionRenderer: React.FC<SectionRendererProps> = ({
  section,
  sectionScores,
  localChecklist,
  isLogSubmitted,
  onResponseToItem,
  automations,
  childItemsMap,
  lastUpdatedRef,
  onNotePostToItem,
  handleUpdateItemNote,
  handleDeleteChecklistItemNote,
  handleCreateCorrectiveTask,
  itemErrors,
  handleShowUploadModal,
  isActionsAllowed,
  taskLocation,
  isMobileDeviceMd,
  checkVisibility,
  parentSection,
  checkListLogHashes,
  checklistProgAndScore,
  isSectionVisible,
  updateMultipleItems,

  isReviewMode,
  canReview,
  feedbackActions,
}) => {
  const { progress, score } = sectionScores?.[section?.id] || {};

  useEffect(() => {
    if (isSectionVisible) {
      if (section.type === STEP_TYPES.HEADER) {
        updateMultipleItems?.(
          section?.items
            ? section?.items.reduce((ids, item) => {
                if (
                  item.type === STEP_TYPES.NUMBER ||
                  item.type === STEP_TYPES.TEMPERATURE ||
                  item.type === STEP_TYPES.COST ||
                  item.type === STEP_TYPES.CALCULATION
                ) {
                  ids.push(item.id);
                }
                return ids;
              }, [])
            : [],
          {
            isHidden: false,
          },
        );
      }
    } else {
      if (section.type === STEP_TYPES.HEADER) {
        updateMultipleItems?.(
          section?.items
            ? section?.items.reduce((ids, item) => {
                if (
                  item.type === STEP_TYPES.NUMBER ||
                  item.type === STEP_TYPES.TEMPERATURE ||
                  item.type === STEP_TYPES.COST ||
                  item.type === STEP_TYPES.CALCULATION
                ) {
                  ids.push(item.id);
                }
                return ids;
              }, [])
            : [],
          {
            isHidden: true,
          },
        );
      }
    }
  }, [isSectionVisible]);

  if (!section) return null;

  const renderSectionHeader = (parentSection) => (
    <AccordionSummary
      expandIcon={<ExpandMoreIcon className="expandIcon" />}
      aria-controls="panel1-content"
      className="sectionAccordionSummary"
    >
      <Stack
        width="100%"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap={'wrap'}
        gap={isMobileDeviceMd ? '8px' : '0px'}
      >
        {parentSection ? (
          <Stack direction="column" gap="5px">
            <Typography className="parentSectionTitle">
              {parentSection?.title}
            </Typography>
            <Stack direction="row" gap="3px">
              <ConnectIcon className="connectIcon" />
              <Typography className="sectionTitle">
                {section.title || section.description} (
                {progress?.completed || '0'} of {progress?.total || '0'})
              </Typography>
            </Stack>
          </Stack>
        ) : (
          <Typography className="sectionTitle">
            {section.title || section.description}{' '}
            {(progress?.total || 0) > 0 && (
              <>
                {' '}
                ({progress?.completed || '0'} of {progress?.total || '0'})
              </>
            )}
          </Typography>
        )}

        {localChecklist?.Checklist?.isShowScoreInProgress && score?.total >= 0 && (
          <Chip
            label={`Score: ${score?.percentage}% (${score?.earned} of ${score?.total} pts)`}
            size="small"
            sx={{
              background: '#CCCBFF',
              fontWeight: 700,
              fontSize: '13px',
            }}
          />
        )}
      </Stack>
    </AccordionSummary>
  );

  const renderItems = (items: any[]) => {
    if (!items) return null;
    return items.map((itemTemp) => {
      const item = localChecklist?.TaskChecklistItems?.find(
        (item) => item?.id === itemTemp?.id,
      );

      const isItemVisible = checkVisibility?.(item);

      return (
        <div key={item?.id}>
          <Box
            ref={
              item?.updatedAt === localChecklist?.lastItemUpdatedAt
                ? lastUpdatedRef
                : null
            }
          >
            <TemplateItem
              checklistProgAndScore={checklistProgAndScore}
              localChecklist={localChecklist}
              onResponseToItem={onResponseToItem}
              isMobileDeviceMd={isMobileDeviceMd}
              checkVisibility={checkVisibility}
              showTimeStamp={localChecklist?.Checklist?.isShowTimestamp}
              isLogSubmitted={isLogSubmitted}
              onChangeCb={onResponseToItem}
              item={item}
              automations={automations}
              childItemsMap={childItemsMap}
              lastUpdatedRef={lastUpdatedRef}
              onNotePostToItem={onNotePostToItem}
              handleUpdateItemNote={handleUpdateItemNote}
              handleDeleteChecklistItemNote={handleDeleteChecklistItemNote}
              handleCreateCorrectiveTask={handleCreateCorrectiveTask}
              itemErrors={itemErrors}
              handleShowUploadModal={handleShowUploadModal}
              isActionsAllowed={isActionsAllowed}
              taskLocation={taskLocation}
              checkListLogHashes={checkListLogHashes}
              updateMultipleItems={updateMultipleItems}
              isItemVisible={isItemVisible}
              isReviewMode={isReviewMode}
              canReview={canReview}
              feedbackActions={feedbackActions}
            />
          </Box>
        </div>
      );
    });
  };

  const renderSubSections = (subSections: any[], parentSection: any) => {
    if (!subSections) return null;
    return subSections.map((subSection) => {
      const isSubSectionVisible = checkVisibility?.(subSection);

      return (
        <SectionRenderer
          key={subSection.id}
          section={subSection}
          sectionScores={sectionScores}
          localChecklist={localChecklist}
          isLogSubmitted={isLogSubmitted}
          onResponseToItem={onResponseToItem}
          automations={automations}
          childItemsMap={childItemsMap}
          lastUpdatedRef={lastUpdatedRef}
          onNotePostToItem={onNotePostToItem}
          handleUpdateItemNote={handleUpdateItemNote}
          handleDeleteChecklistItemNote={handleDeleteChecklistItemNote}
          handleCreateCorrectiveTask={handleCreateCorrectiveTask}
          itemErrors={itemErrors}
          handleShowUploadModal={handleShowUploadModal}
          isActionsAllowed={isActionsAllowed}
          taskLocation={taskLocation}
          isMobileDeviceMd={isMobileDeviceMd}
          checkVisibility={checkVisibility}
          parentSection={parentSection}
          checklistProgAndScore={checklistProgAndScore}
          isSectionVisible={isSubSectionVisible}
          updateMultipleItems={updateMultipleItems}
          checkListLogHashes={checkListLogHashes}
          isReviewMode={isReviewMode}
          canReview={canReview}
          feedbackActions={feedbackActions}
        />
      );
    });
  };

  return isSectionVisible ? (
    <TemplatePreview
      sx={{
        ...(parentSection && {
          marginLeft: '20px !important',
        }),
      }}
    >
      <Accordion
        className="sectionAccordion"
        TransitionProps={{ unmountOnExit: true }}
        defaultExpanded
        disableGutters
        square={false}
      >
        {renderSectionHeader(parentSection)}
        <AccordionDetails sx={{ px: 0, pb: 0 }}>
          <Stack spacing={2}>
            {renderItems(section?.TaskChecklistItems)}
            {renderSubSections(section?.subSections, section)}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </TemplatePreview>
  ) : (
    <></>
  );
};

export default SectionRenderer;
